import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { inject, observer } from "mobx-react";
import { computed } from "mobx";

import { Chip } from "primereact/chip";
import { Sidebar } from "primereact/sidebar";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { Message } from "primereact/message";

import { pluralForm, changeValByPath, updateParamWithoutRerender, clearParamWithoutRerender } from "@utils";

import "./style.scss";
import { confirmDialog } from "primereact/confirmdialog";
import TooltipUI from "@UI/TooltipUI";
import ButtonUI from "@UI/ButtonUI";

const OfferSortConfig = inject("mainStore")(
    observer(({ mainStore, getButtons, disabled }) => {
        const detailData = computed(() => mainStore.getMainStore("detailData")).get();
        const { secondParam } = useParams();

        // при создании создаем новый офер и переходим на детальную для дальнейшего редактирования
        if (secondParam == "add")
            return getButtons()?.map(
                (btn) =>
                    btn.id == "draft" && (
                        <ButtonUI
                            style={{ width: "100%" }}
                            key={btn.label}
                            label="Добавить сортировки"
                            onClick={async () => {
                                await btn.action();
                                setTimeout(() => updateParamWithoutRerender("scrollTo", "offer-sort-config"), 100); // создаем параметр для подскрола
                            }}
                        />
                    ),
            );
        // при создании создаем новый офер и переходим на детальную для дальнейшего редактирования

        const [sorts, setSorts] = useState([]);
        const [options, setOptions] = useState({});
        const [visible, setVisible] = useState(false);

        useEffect(() => {
            //ловим параметр для подскрола
            setTimeout(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const scrollTo = urlParams.get("scrollTo");
                if (scrollTo) {
                    document.getElementById("offer-sort-config").scrollIntoView({ behavior: "smooth", block: "center" });
                    clearParamWithoutRerender(["scrollTo"]);
                }
            }, 1000);
            //ловим параметр для подскрола
            getSorts();
        }, []);

        const getSorts = async () => {
            const [sort, opt] = await Promise.all([
                mainStore.getByUrl(`/promo/offerbysitesorts`, { filters: { offerId: secondParam } }),
                mainStore.getByUrl(`/promo/suggest_list?collection=sites&project[_id]=1&project[code]=1&project[active]=1&project[name]=1&sortField=name&sortOrder=asc&project[siteInfo]=1&filters[active]=true`),
            ]);

            if (sort.success && opt.success) {
                const modifiedOptions = [...opt.data];
                const vendorSet = new Set(detailData?.vendor?.categoriesIds || []);
                modifiedOptions.forEach((i) => {
                    const a = i.siteInfo?.categoriesIds;
                    if (a?.some((id) => vendorSet.has(id))) i.areSame = true;
                });
                setSorts(sort.data);
                setOptions(Object.fromEntries(modifiedOptions.map((row) => [row._id, row])));
            }
        };

        return (
            <div id="offer-sort-config" className="offer-sort-config">
                <div className="offer-sort-config__chips-wrapper">
                    {sorts.map((i) => options?.[i.siteId] && <Chip key={i._id} label={`${options[i.siteId]?.name || i.siteId} (${i.sort})`} className="disabled-chip" style={{ background: options[i.siteId]?.areSame ? "var(--orange-100)" : "" }} />)}
                </div>

                {sorts.some((s) => options?.[s.siteId]?.areSame) && (
                    <Message
                        severity="warn"
                        text="Вы выбрали витрину из схожей категории — проверьте, можно ли использовать его для публикации оффера."
                        style={{
                            border: "solid #CC8925",
                            borderWidth: "0 0 0 6px",
                            color: "#CC8925",
                            padding: "17.5px 24.5px 17.5px",
                            justifyContent: "flex-start",
                        }}
                    />
                )}

                <div className="offer-sort-config__actions">
                    <ButtonUI label="Редактировать" icon="pi pi-pencil" onClick={() => setVisible(true)} disabled={disabled} />
                </div>

                <SortSideBar visible={visible} setVisible={setVisible} sorts={sorts} secondParam={secondParam} getSorts={getSorts} options={options} />
            </div>
        );
    }),
);
const SortSideBar = inject("mainStore")(
    observer(({ mainStore, visible = true, setVisible = () => {}, sorts = [], secondParam = "", getSorts = () => {}, options = null }) => {
        const [singleVal, setSingleVal] = useState(0);
        const [selected, setSelected] = useState([]);
        const [unchangeableSelected, setUnchangeableSelected] = useState([]);

        useEffect(() => {
            if (!options || !sorts.length) return;

            const mapped = sorts.reduce((acc, row) => {
                if (options[row.siteId]) acc.push({ site: options[row.siteId], sort: row.sort });
                return acc;
            }, []);

            setSelected(mapped);
            setUnchangeableSelected(mapped);
        }, [options, sorts]);

        const handleSortChange = (e, idx) => setSelected((prev) => changeValByPath(`${idx}.sort`, e.value, prev));
        const handleApplySingle = () => setSelected((prev) => prev.map((r) => ({ ...r, sort: singleVal || 0 })));
        const handleDelete = (siteId) => setSelected((prev) => prev.filter((r) => r.site._id !== siteId));

        const handleSelect = (e) => {
            const nextArr = e.value || [];
            const current = e.selectedOption;

            setSelected((prev) => {
                if (!nextArr.length) return [];

                const prevSorts = new Map(prev.map((r) => [r.site._id, r.sort]));
                const isAdded = nextArr.some((s) => s._id === current._id);
                const orderedSites = isAdded ? [current, ...nextArr.filter((s) => s._id !== current._id)] : nextArr;

                return orderedSites.map((site) => ({ site, sort: prevSorts.get(site._id) || 0 }));
            });
        };

        const handleSave = async () => {
            const selectedSetted = new Set(selected.map((i) => i.site._id));

            const delData = [];
            // выделяем только изменные сортировки
            const changedData = selected.filter(({ site, sort }) => {
                const prev = sorts.find((s) => s.siteId === site._id);
                return !prev || prev.sort !== sort;
            });
            const newData = changedData.map(({ site, sort }) => ({ siteId: site._id, offerId: secondParam, sort }));

            sorts.forEach((i) => !selectedSetted.has(i.siteId) && delData.push({ _id: i._id }));

            console.log("sort delData", delData);
            console.log("sort newData", newData);

            if (delData.length) {
                const respDel = await mainStore.postByUrl("/promo/offerbysitesortsmany", { type: "delete", data: delData });
                if (!respDel.success) {
                    console.error("удаление сортировки не прошло");
                    return; // выходим если удаление не отработало и не идем на сохранение
                }
            }

            if (newData.length) {
                const newResp = await mainStore.postByUrl("/promo/offerbysitesortsmany", { data: newData });
                if (!newResp.success) {
                    console.error("добавление сортировки не прошло");
                    return; // выходим если добавление не отработало
                }
            }

            if (delData.length || newData.length) mainStore.setMainStore("toast", { severity: "success", summary: "Сортировки успешно обновлены" });
            getSorts();
            setVisible(false);
        };

        const confirm = () => {
            confirmDialog({
                message: "Ваши изменения не были сохранены",
                header: "Сохранить изменения?",
                icon: "pi pi-exclamation-triangle",
                defaultFocus: "accept",
                rejectLabel: "Не сохранять",
                acceptLabel: "Сохранить",
                rejectClassName: "p-button-danger",
                accept: () => handleSave(),
                reject: () => handleReject(),
            });
        };

        const handleReject = () => {
            setVisible(false);
            setSelected(unchangeableSelected);
            setSingleVal(0);
        };

        const diffData = JSON.stringify(selected) === JSON.stringify(unchangeableSelected);

        return (
            <Sidebar visible={visible} position="right" className="offer-sort-config__sidebar sort-sidebar" onHide={() => (!diffData ? confirm() : setVisible(false))}>
                <div className="sort-sidebar__content">
                    <h3>Редактирование сортировок</h3>

                    <MultiSelect
                        filter
                        value={selected.map((r) => r.site)}
                        options={Object.values(options)}
                        optionLabel="name"
                        placeholder="Выберите витрины"
                        maxSelectedLabels={0}
                        className="w-full md:w-20rem"
                        onChange={handleSelect}
                        selectedItemsLabel={`Выбрано ${selected.length} витрин${pluralForm(selected.length, ["а", "ы", ""])}`}
                        itemTemplate={(i) => <span style={{ color: i.areSame ? "#CC8925" : "" }}>{i.name}</span>}
                    />

                    {!!selected.length && (
                        <div className="sort-sidebar__single-val mt-3">
                            <div className="p-inputgroup flex-1">
                                <InputNumber locale="ru-RU" min={0} value={singleVal} onValueChange={(e) => setSingleVal(e.value)} />
                                <ButtonUI className="p-button-warning" onClick={handleApplySingle} label="Применить ко всем" />
                            </div>
                        </div>
                    )}

                    <div className="sort-sidebar__edit-list">
                        {selected.map((r, idx) => (
                            <div key={r.site._id} className="sort-sidebar__sort-item">
                                <TooltipUI target={`#tooltip-${r.site._id}`} position="left" />
                                <div id={`tooltip-${r.site._id}`} className="sort-sidebar__id" data-pr-tooltip={r.site.name} style={{ color: r.site.areSame ? "#CC8925" : "" }}>
                                    {r.site.name}
                                </div>
                                <InputNumber locale="ru-RU" min={0} value={r.sort} onChange={(e) => handleSortChange(e, idx)} showButtons className="sort-sidebar__sort-input" />
                                <ButtonUI icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => handleDelete(r.site._id)} />
                            </div>
                        ))}
                    </div>

                    <div className="sort-sidebar__footer">
                        <ButtonUI label="Сохранить" icon="pi pi-check" onClick={handleSave} className="p-button-success" disabled={!selected.length} />
                    </div>
                </div>
            </Sidebar>
        );
    }),
);

export default OfferSortConfig;
